import { useEffect, useRef } from 'react'
import i18n from 'i18next'
import type { ClauseLine } from '../../engine/types'

// Random filler characters: code symbols + digits + section symbols
const FILLER_CHARS = '{}[]<>/*=+;:()0123456789§¶#@&%!?~'

// Fallback phrases when no clauses provided (loaded from i18n at runtime)
function getFallbackPhrases(): string[] {
  return i18n.t('codeRain.phrases', { returnObjects: true }) as string[]
}

// Color palette matching the green theme
const HEAD_COLOR = '#aaffcc'
const BRIGHT_COLOR = '#4aaa75'
const MID_COLOR = '#2a7a55'
const DIM_COLOR = '#1a4a35'

// Background fade color (matches gradient midpoint)
const FADE_BG = 'rgba(15,42,30,0.15)'

const TARGET_INTERVAL = 50 // ~20fps for classic Matrix stutter
const FONT_SIZE = 14
const CANVAS_SCALE = 0.5 // Render at half resolution
// CJK chars are full-width — column width must be >= font size
const COL_WIDTH_FACTOR = 1.2
// Font stack with CJK support for canvas rendering
const FONT_NORMAL = `${FONT_SIZE}px "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Noto Sans SC", monospace`
const FONT_BOLD = `bold ${FONT_SIZE}px "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Noto Sans SC", monospace`

// Probability that a column carries a phrase (vs pure random chars)
const PHRASE_CHANCE = 0.4

interface Column {
  y: number
  speed: number
  trailLen: number
  chars: string[]
  phraseStart: number // trail offset where phrase begins (-1 = none)
  phraseLen: number
}

/** Build a phrase pool from the current level's clause data */
function buildPhrases(clauses: ClauseLine[]): string[] {
  if (clauses.length === 0) return getFallbackPhrases()

  const phrases: string[] = []

  for (const c of clauses) {
    // Clause full text — strip 「」 brackets
    const raw = c.text.replace(/[「」『』]/g, '')
    // Split long text into chunks of ~6-10 chars for vertical readability
    if (raw.length <= 12) {
      phrases.push(raw)
    } else {
      // Split at punctuation or every ~8 chars
      const chunks = raw.match(/[^，。、；：]+[，。、；：]?/g) || [raw]
      for (const chunk of chunks) {
        const trimmed = chunk.replace(/[，。、；：]$/, '')
        if (trimmed.length > 0 && trimmed.length <= 14) {
          phrases.push(trimmed)
        } else if (trimmed.length > 14) {
          // Further split long chunks
          for (let i = 0; i < trimmed.length; i += 8) {
            const sub = trimmed.slice(i, i + 8)
            if (sub.length >= 2) phrases.push(sub)
          }
        }
      }
    }

    // shortText as-is (concise label)
    if (c.shortText) phrases.push(c.shortText)

    // Client arguments — the opponent's lines
    for (const arg of c.client_arguments) {
      const argText = arg.text.replace(/[「」『』""]/g, '')
      if (argText.length <= 12) {
        phrases.push(argText)
      } else {
        const parts = argText.match(/[^，。、；：！？]+[，。、；：！？]?/g) || [argText]
        for (const part of parts) {
          const t = part.replace(/[，。、；：！？]$/, '')
          if (t.length >= 2 && t.length <= 14) phrases.push(t)
        }
      }
    }

    // Clause ID (e.g. "CL01")
    phrases.push(c.id)
  }

  // Deduplicate
  return [...new Set(phrases)]
}

function randomFiller(): string {
  return FILLER_CHARS[Math.floor(Math.random() * FILLER_CHARS.length)]!
}

function buildColumnChars(rows: number, phraseInfo: { start: number; phrase: string } | null): string[] {
  const chars: string[] = Array.from({ length: rows }, () => randomFiller())
  if (phraseInfo) {
    const { start, phrase } = phraseInfo
    for (let i = 0; i < phrase.length; i++) {
      const idx = start + i
      if (idx >= 0 && idx < rows) {
        chars[idx] = phrase[i]!
      }
    }
  }
  return chars
}

function initColumn(rows: number, phrases: string[]): Column {
  const hasPhrase = Math.random() < PHRASE_CHANCE && phrases.length > 0
  const phrase = hasPhrase ? phrases[Math.floor(Math.random() * phrases.length)] : null
  const trailLen = 8 + Math.floor(Math.random() * 16)
  const phraseStart = phrase ? Math.floor(Math.random() * Math.max(1, trailLen - phrase.length)) : -1

  return {
    y: -Math.floor(Math.random() * rows),
    speed: 1 + Math.random() * 2,
    trailLen,
    chars: buildColumnChars(rows, phrase ? { start: phraseStart, phrase } : null),
    phraseStart: phrase ? phraseStart : -1,
    phraseLen: phrase ? phrase.length : 0,
  }
}

function initColumns(cols: number, rows: number, phrases: string[]): Column[] {
  return Array.from({ length: cols }, () => initColumn(rows, phrases))
}

interface CodeRainCanvasProps {
  clauses?: ClauseLine[]
}

export function CodeRainCanvas({ clauses = [] }: CodeRainCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const phrasesRef = useRef<string[]>(getFallbackPhrases())

  // Rebuild phrase pool when clauses change (typically once on mount)
  useEffect(() => {
    phrasesRef.current = buildPhrases(clauses)
  }, [clauses])

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let columns: Column[] = []
    let cols = 0
    let rows = 0
    let rafId = 0
    let lastTime = 0

    function resize() {
      const rect = container!.getBoundingClientRect()
      const w = Math.floor(rect.width * CANVAS_SCALE)
      const h = Math.floor(rect.height * CANVAS_SCALE)
      canvas!.width = w
      canvas!.height = h
      cols = Math.floor(w / (FONT_SIZE * COL_WIDTH_FACTOR))
      rows = Math.floor(h / FONT_SIZE) + 2
      columns = initColumns(cols, rows, phrasesRef.current)

      ctx!.fillStyle = '#0F2A1E'
      ctx!.fillRect(0, 0, w, h)
    }

    resize()

    const observer = new ResizeObserver(resize)
    observer.observe(container)

    function drawFrame() {
      const w = canvas!.width
      const h = canvas!.height

      ctx!.fillStyle = FADE_BG
      ctx!.fillRect(0, 0, w, h)

      const colWidth = w / cols

      for (let i = 0; i < cols; i++) {
        const col = columns[i]!
        const headY = Math.floor(col.y)

        for (let t = 0; t < col.trailLen; t++) {
          const charY = headY - t
          if (charY < 0 || charY >= rows) continue

          const px = i * colWidth
          const py = charY * FONT_SIZE

          const isPhrase = col.phraseStart >= 0 && t >= col.phraseStart && t < col.phraseStart + col.phraseLen

          // Only mutate non-phrase characters
          if (!isPhrase && Math.random() < 0.08) {
            col.chars[charY] = randomFiller()
          }

          // Color based on position in trail
          if (t === 0) {
            ctx!.fillStyle = HEAD_COLOR
            ctx!.font = FONT_NORMAL
          } else if (isPhrase) {
            ctx!.fillStyle = BRIGHT_COLOR
            ctx!.font = FONT_BOLD
          } else if (t < 3) {
            ctx!.fillStyle = BRIGHT_COLOR
            ctx!.font = FONT_NORMAL
          } else if (t < col.trailLen * 0.6) {
            ctx!.fillStyle = MID_COLOR
            ctx!.font = FONT_NORMAL
          } else {
            ctx!.fillStyle = DIM_COLOR
            ctx!.font = FONT_NORMAL
          }

          ctx!.textBaseline = 'top'
          ctx!.fillText(col.chars[charY]!, px, py)
        }

        col.y += col.speed

        if (col.y - col.trailLen > rows) {
          const c = initColumn(rows, phrasesRef.current)
          c.y = -Math.floor(Math.random() * rows * 0.5)
          columns[i] = c
        }
      }
    }

    if (reducedMotion) {
      for (let i = 0; i < 30; i++) drawFrame()
      return () => observer.disconnect()
    }

    function animate(time: number) {
      if (time - lastTime >= TARGET_INTERVAL) {
        lastTime = time
        drawFrame()
      }
      rafId = requestAnimationFrame(animate)
    }

    rafId = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(rafId)
      observer.disconnect()
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 0, opacity: 0.12 }}
    >
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
    </div>
  )
}
